import moment from 'moment'
import { FASTQUOTE_NEW_START_DATE } from 'src/config'
import {
  agency_t,
  cookie_informative,
  dict,
  fitem_t,
  municipality_t,
  opt,
  option_t,
  otherdoc_list_t,
  otherdoc_t,
  parameter_t,
  product_t,
  quote_t,
  quotedoc_t,
  redux_store,
  vehicle_brand_t,
  vehicle_model_t,
  vehicle_version_t,
} from 'src/types'
import { deep_copy, find_fitem, find_foption, find_option } from 'src/utils'

export const select_formik_pool = (state: redux_store): opt<fitem_t[]> => state?.data?.formik_pool

export const select_formik_pool_completed = (state: redux_store): boolean =>
  (state?.data?.formik_pool?.length || 0) > 11

export const select_formik_item = (formik_id: string) => (state: redux_store): opt<fitem_t> =>
  find_fitem(select_formik_pool(state), formik_id)

export const select_is_fastquote = (state: redux_store): boolean => {
  return select_formik_item('is3__plate_present')(state)?.capture === 'yes'
}

export const select_company = (state: redux_store): opt<option_t> => {
  const is_fastquote = select_is_fastquote(state)
  const company = select_formik_item(is_fastquote ? 'fs1b__company' : 'ts1b__company')(state)
  return find_option(company?.options, company?.capture)
}

export const select_ts3_visible = (state: redux_store): boolean => {
  const sector = find_foption(select_formik_pool(state), 'ts1a__vehicle_type')?.key
  const mop_entity = find_foption(select_formik_pool(state), 'ts2__mop__entity')?.key
  return (
    sector !== '1001-3' &&
    sector !== '1001-6' &&
    sector !== '1001-7' &&
    sector !== '1001-8' &&
    sector !== '1001-9' &&
    mop_entity !== '36'
  )
}

export const select_ts4_visible = (state: redux_store): boolean => {
  const sector = find_foption(select_formik_pool(state), 'ts1a__vehicle_type')?.key
  return sector === '1001-1' || sector === '1001-4' || sector === '1001-5'
}

export const select_metadata = (state: redux_store): opt<string> => state?.data?.metadata

export const select_all_products = (state: redux_store): opt<product_t[]> => state?.data?.products

export const select_current_products = (state: redux_store): opt<product_t[]> => {
  let current_prods: dict<product_t> = {}

  select_all_products(state)?.forEach((prod) => {
    const prev = current_prods?.[prod?.product_code || '']
    const prev_date = moment(prev?.start_date).format('YYYY-MM-DD')
    const curr_date = moment(prod?.start_date).format('YYYY-MM-DD')
    const today = moment().format('YYYY-MM-DD')
    if (!prev || (curr_date > prev_date && curr_date <= today)) {
      current_prods = {
        ...current_prods,
        [prod?.product_code || '']: prod,
      }
    }
  })

  return Object.values(current_prods)
}

export const select_start_date = (state: redux_store): opt<string> => {
  const f_prod = select_formik_item('ts1a__vehicle_type')(state)
  const p_prods = deep_copy(
    select_all_products(state)?.filter(({ product_code }) => product_code === f_prod?.capture)
  )
  const sorted_prods = p_prods?.sort(({ start_date: sd1 = '' }, { start_date: sd2 = '' }) =>
    Date.parse(sd1) < Date.parse(sd2) ? -1 : 1
  )
  const old_start_date = sorted_prods?.[0]?.start_date || ''
  const new_start_date = sorted_prods?.[1]?.start_date || ''
  if (!new_start_date) {
    return old_start_date
  }
  const effective_date = moment(select_effective_date(state), 'DD/MM/YYYY').format('YYYY-MM-DD')
  const is_fastquote = select_is_fastquote(state)
  if (FASTQUOTE_NEW_START_DATE && is_fastquote) {
    return new_start_date
  }
  return effective_date < new_start_date?.split('T')?.[0] ? old_start_date : new_start_date
}

export const select_vehicle_brands = (state: redux_store): opt<vehicle_brand_t[]> =>
  state?.data?.vehicle_brands

export const select_vehicle_mop_brands = (state: redux_store): opt<vehicle_brand_t[]> =>
  state?.data?.vehicle_mop_brands

export const select_vehicle_models = (state: redux_store): opt<vehicle_model_t[]> =>
  state?.data?.vehicle_models

export const select_vehicle_mop_models = (state: redux_store): opt<vehicle_model_t[]> =>
  state?.data?.vehicle_mop_models

export const select_vehicle_versions = (state: redux_store): opt<vehicle_version_t[]> =>
  state?.data?.vehicle_versions

export const select_residence_municipalities = (state: redux_store): opt<municipality_t[]> =>
  state?.data?.residence_municipalities

export const select_quote = (state: redux_store): opt<quote_t> => state?.data?.quote

export const select_agencies = (state: redux_store): opt<agency_t[]> => state?.data?.agencies

export const select_quotedoc = (state: redux_store): opt<quotedoc_t> => state?.data?.quotedoc

export const select_otherdocs = (state: redux_store): opt<otherdoc_list_t[]> =>
  state?.data?.otherdocs

export const select_otherdoc = (state: redux_store): opt<otherdoc_t> => state?.data?.otherdoc

// TODO: Rimuovere
export const select_municipalities_temp = (state: redux_store): opt<dict<option_t[]>> =>
  state?.data?.municipalities_temp

export const select_effective_date = (state: redux_store): string | undefined => {
  const effective_date = select_formik_item('ts1a__effective_date')(state)?.capture
  return effective_date
}

export const select_cookie_informatives = (state: redux_store): opt<cookie_informative[]> =>
  state?.data?.cookie_informatives

export const select_documents = (state: redux_store): opt<parameter_t[]> => state?.data?.documents

export const select_document = (state: redux_store): opt<parameter_t> => state?.data?.document
